import IModel from "./IModel";
import User, { WorkTime } from "./User";

export interface NearbyAttractionItem {
	image: string;
	title: string;
	walkingMinutes: number;
	description: string;
}

function normalizeHourRange(value: any) {
	if (!value) return null;

	if (typeof value === "string") {
		const trimmedValue = value.trim();
		if (!trimmedValue) return null;

		const parts = trimmedValue.split("-").map((item) => item.trim());
		if (parts.length === 2 && parts[0] && parts[1]) {
			return {
				from: parts[0],
				to: parts[1],
			};
		}

		return null;
	}

	if (typeof value === "object") {
		const from = typeof value.from === "string" ? value.from.trim() : "";
		const to = typeof value.to === "string" ? value.to.trim() : "";
		if (from && to) {
			return { from, to };
		}
	}

	return null;
}

function normalizeWorkTimeValue(value: any): WorkTime[] | null {
	if (!value) return null;

	if (typeof value === "string") {
		try {
			return normalizeWorkTimeValue(JSON.parse(value));
		} catch (_error) {
			const hourRange = normalizeHourRange(value);
			return hourRange ? [{ hour: hourRange } as WorkTime] : null;
		}
	}

	if (Array.isArray(value)) {
		const normalized = value
			.map((item) => {
				const hour = normalizeHourRange(item?.hour) || normalizeHourRange(item);
				if (!hour) return null;

				return {
					...item,
					hour,
				} as WorkTime;
			})
			.filter(Boolean) as WorkTime[];

		return normalized.length ? normalized : null;
	}

	if (typeof value === "object") {
		const directHour = normalizeHourRange(value?.hour) || normalizeHourRange(value);
		if (directHour) {
			return [
				{
					...value,
					hour: directHour,
				} as WorkTime,
			];
		}

		const firstMappedDay = Object.entries(value).find(([, dayValue]) => normalizeHourRange(dayValue));
		if (firstMappedDay) {
			const [dayKey, dayValue] = firstMappedDay;
			const hour = normalizeHourRange(dayValue);
			if (hour) {
				return [
					{
						date: {
							from: dayKey,
							to: dayKey,
						},
						hour,
					} as WorkTime,
				];
			}
		}
	}

	return null;
}

/**
 * Trạng thái vòng đời station — Quân chốt 25/08.
 *
 * Giá trị TRÙNG KHÍT cột `lifecycleStatus` trong DB để không ai phải dịch ngược khi
 * soi dữ liệu. `Banned` là giá trị MỚI: trước đây server không bao giờ trả nó, station
 * bị cấm bị gọi nhầm là `inactive` vì lệnh ban ghi `active = false` kèm theo.
 * Nguyên văn Quân: *"Inactive là ko còn nợ nần gì nữa thôi, Banned là cấm do 1 vài lý do"*.
 *
 * `Actived` giữ nguyên chữ của DB (`actived`) chứ không sửa thành `Active` cho đúng
 * ngữ pháp — sai một chữ đổi lấy việc nhãn và dữ liệu luôn đọc giống nhau.
 */
export enum PartnerStatus {
	All = "",
	/**
	 * Hồ sơ đã nộp, CHƯA ai duyệt. Thiếu mục này là 5 hồ sơ `pending` trên
	 * production rơi vào nhánh dự phòng và bị hiện nhầm thành "Inactive"
	 * (đo 28/08/2026) — nhân viên nhìn tưởng station đã đóng, không ai đi duyệt.
	 */
	/** @deprecated Giá trị CŨ. Quân chốt 28/08 tách thành `Approved` + `Paused`. */
	Pending = "pending",
	/** @deprecated Giá trị CŨ, còn dữ liệu chưa migrate thì vẫn đọc được. */
	Actived = "actived",
	/** Đã duyệt nhưng CHƯA từng đủ điều kiện bán (thiếu toạ độ). */
	Approved = "approved",
	/** Đã đủ điều kiện, đang chủ động ngừng bán (đóng cửa). */
	Paused = "paused",
	Published = "published",
	UnActive = "inactive",
	Banned = "banned",
}

// ⚠️ `Pending` CỐ Ý KHÔNG có trong bộ lọc này. Thiết kế 27/08 tách HAI THẾ GIỚI:
// hồ sơ chưa duyệt sống ở màn **Station Requests**, còn Station Management chỉ dành
// cho station đã là điểm bán. Đưa "Need Approval" vào đây là trộn lại hai thế giới.
// Nhãn bên dưới vẫn giữ, để dòng `pending` nào lỡ hiện ra thì đọc đúng chữ chứ
// không bị ghi nhầm thành "Inactive".
export const PartnerStatusList = [
	PartnerStatus.All,
	PartnerStatus.Approved,
	PartnerStatus.Paused,
	PartnerStatus.Published,
	PartnerStatus.UnActive,
	PartnerStatus.Banned,
];

/**
 * Nhãn hiển thị (Quân chốt 27/08/2026). GIÁ TRỊ gửi lên API giữ nguyên
 * ("actived" / "inactive" / "published" / "banned") — chỉ đổi chữ trên màn hình, nên
 * link và bookmark cũ không chết.
 *
 * Vì sao đổi hai cái tên: "actived" nghĩa là station còn hợp tác nhưng KHÔNG bán được
 * (thiếu toạ độ, hoặc đang đóng cửa) — nhìn chữ "Actived" ai cũng tưởng đang chạy tốt,
 * ngược hẳn trực giác. "Inactive" thì nghe như tự nhiên im, thực chất là có người bấm
 * tắt. Cặp Paused/Closed là đúng cặp Google Maps dùng cho mọi cơ sở kinh doanh.
 */
export const PartnerStatusLabelMap = new Map([
	[PartnerStatus.All, "All"],
	[PartnerStatus.Pending, "Need Approval"],
	[PartnerStatus.Approved, "Approved"],
	[PartnerStatus.Paused, "Paused"],
	// Giá trị cũ — giữ nhãn để dòng chưa migrate vẫn đọc được chữ, không hiện giá trị thô.
	[PartnerStatus.Actived, "Paused"],
	[PartnerStatus.Published, "Published"],
	[PartnerStatus.UnActive, "Closed"],
	[PartnerStatus.Banned, "Banned"],
]);

/**
 * 28/10/2026 (Quân chốt): hồ sơ CHỦ CHUỖI thuần (tách khỏi trạm — vai StoreOwner, thuộc một chuỗi,
 * không toạ độ, không bán) luôn có `active=false` theo thiết kế ⇒ trước đây hiện "Closed", đọc như
 * chuỗi đã đóng. Chỉ đổi CHỮ hiển thị; giá trị lọc gửi API giữ nguyên.
 */
export function laChuChuoiThuan(p: any): boolean {
	if (!p || Number(p.partnerRoleId) !== 2 || !p.storeId) return false;
	if (p.lat && p.long) return false;
	return String(p.status || "").toLowerCase() !== PartnerStatus.Published;
}

export function nhanTrangThaiPartner(p: any): string {
	if (laChuChuoiThuan(p)) return "Chain owner";
	const raw = String((p && p.status) || "").toLowerCase();
	return PartnerStatusLabelMap.get(raw as PartnerStatus) || raw || "—";
}

export default class Partner implements IModel<Partner> {
	id: string | null;
	name: string | null;
	active: boolean | null;
	status?: string | null;
	userId?: string | null;
	address?: string | null;
	addressText?: string | null;
	note?: string | null;
	fullName?: string | null;
	phone?: string | null;
	email?: string | null;
	gender?: number;
	avatar?: string | null;
	displayAddress?: string | null;
	displayName?: string | null;
	type?: number | null;

	rateReview?: number | null;
	reviewCount?: number | null;
	lat?: number | null;
	long?: number | null;
	fee?: number | null;
	createdAt?: Date | null;
	updatedAt?: Date | null;
	expireTime: Date | null;

	workTime?: WorkTime[] | null;
	destinationId?: string | null;
	phonePrefix?: string | null;
	user?: User | null;
	insuranceFee?: string | null;
	destination?: Partner | null;
	country?: string | null;
	currencyCode?: string | null;
	destinationCurrencyCode?: string | null;
	localCurrencyCode?: string | null;
	hotlineNumber?: string[] | null;
	images?: string[] | null;
	/** Station landing — Meet tab photos */
	meetStationImages?: string[] | null;
	/** Station landing — Nearby carousel photos */
	nearbyStationImages?: string[] | null;
	imagesEnabled?: boolean | null;
	walkingDistance?: string | null;
	reviews?: any[] | null;
	store?: Store | null;
	paidAmount?: number | null;
	availableDeductionAmount?: number | null;
	paidAmountLocal?: number | null;
	rateLimitMin?: number | null;
	rateLimitMax?: number | null;
	stationCom?: number | null;
	minimumPayout?: number | null;
	/** When false, station does not use min payout threshold (partner portal + payout logic). */
	minimumPayoutEnabled?: boolean | null;
	/** Non-withdrawable reserve (USD in admin; server converts to local for enforcement). */
	minBalance?: number | null;
	/** When false, min balance reserve is not applied. */
	minBalanceEnabled?: boolean | null;
	maximumPayout?: number | null;
	pubRate?: number | null;
	bankAccountNumber?: string | null;
	bankName?: string | null;
	bankAccountHolder?: string | null;

	/** Admin-only: optional full-day closures + permanent closure (dates in station TZ). */
	closureExceptionConfig?: {
		dateRanges?: { from: string; to: string }[];
		permanentClosure?: { enabled: boolean; effectiveFrom: string | null };
	} | null;

	/** When true, partner cannot create manual payout requests (contract violation). */
	payoutLocked?: boolean | null;
	payoutLockReason?: string | null;
	payoutLockedAt?: string | Date | null;
	payoutLockedBy?: number | null;

	/** Admin-marked HĐ violation level 0–3; shown on workspace station list. */
	contractViolationCount?: number | null;

	/** Customer station landing: Meet your Station tab visible */
	stationShowMeetTab?: boolean | null;
	/** Customer station landing: Nearby Attractions tab visible */
	stationShowNearbyTab?: boolean | null;
	/** Station landing — custom title shown in the Meet your Station card */
	meetStationTitle?: string | null;
	/** Station landing — custom subtitle shown in the Meet your Station card */
	meetStationSubtitle?: string | null;
	/** Station landing — static map image URL */
	mapImage?: string | null;
	/** Station landing — Nearby Attractions items */
	nearbyAttractionsContent?: NearbyAttractionItem[] | null;

	isBanned?: boolean | null;
	banReason?: string | null;
	bannedAt?: string | Date | null;
	bannedBy?: number | null;

	constructor(input?: Partial<Partner>) {
		this.id = input?.id || null;
		this.name = input?.name || null;
		this.active = input?.active || null;
		this.status = input?.status ?? null;
		this.userId = input?.userId;
		this.address = input?.address;
		this.addressText = input?.addressText;
		this.note = input?.note;
		this.displayAddress = input?.displayAddress;
		this.displayName = input?.displayName;
		this.type = input?.type;

		this.rateReview = input?.rateReview;
		this.reviewCount = input?.reviewCount;
		this.lat = input?.lat;
		this.long = input?.long;
		this.fee = input?.fee;

		this.workTime = input?.workTime;
		this.destinationId = input?.destinationId;
		this.createdAt = input?.createdAt;
		this.updatedAt = input?.updatedAt;
		this.expireTime = input?.expireTime || null;
		this.phonePrefix = input?.phonePrefix;
		this.user = input?.user;
		this.insuranceFee = input?.insuranceFee;
		this.destination = input?.destination;
		this.country = input?.country ?? null;
		this.currencyCode = input?.currencyCode ?? null;
		this.destinationCurrencyCode = input?.destinationCurrencyCode ?? null;
		this.localCurrencyCode = input?.localCurrencyCode ?? null;
		this.hotlineNumber = input?.hotlineNumber || [];
		this.images = input?.images || [];
		this.meetStationImages = input?.meetStationImages ?? [];
		this.nearbyStationImages = input?.nearbyStationImages ?? [];
		this.imagesEnabled = input?.imagesEnabled ?? false;
		this.walkingDistance = input?.walkingDistance || null;
		this.reviews = input?.reviews || [];
		this.store = input?.store;
		this.paidAmount = input?.paidAmount ?? null;
		this.availableDeductionAmount = input?.availableDeductionAmount ?? null;
		this.paidAmountLocal = input?.paidAmountLocal ?? null;
		this.rateLimitMin = input?.rateLimitMin ?? null;
		this.rateLimitMax = input?.rateLimitMax ?? null;
		this.stationCom = input?.stationCom ?? null;
		this.minimumPayout = input?.minimumPayout ?? null;
		this.minimumPayoutEnabled = input?.minimumPayoutEnabled ?? true;
		this.minBalance = input?.minBalance ?? null;
		this.minBalanceEnabled = input?.minBalanceEnabled ?? true;
		this.maximumPayout = input?.maximumPayout ?? null;
		this.pubRate = input?.pubRate ?? null;
		this.bankAccountNumber = input?.bankAccountNumber ?? null;
		this.bankName = input?.bankName ?? null;
		this.bankAccountHolder = input?.bankAccountHolder ?? null;
		this.closureExceptionConfig = input?.closureExceptionConfig ?? null;
		this.payoutLocked = input?.payoutLocked ?? null;
		this.payoutLockReason = input?.payoutLockReason ?? null;
		this.payoutLockedAt = input?.payoutLockedAt ?? null;
		this.payoutLockedBy = input?.payoutLockedBy ?? null;
		this.contractViolationCount = input?.contractViolationCount ?? null;
		this.isBanned = input?.isBanned ?? null;
		this.banReason = input?.banReason ?? null;
		this.bannedAt = input?.bannedAt ?? null;
		this.bannedBy = input?.bannedBy ?? null;
		this.mapImage = input?.mapImage ?? null;
		this.nearbyAttractionsContent = input?.nearbyAttractionsContent ?? null;

		// this.createUserName();
	}

	parse(json: any): Partner {
		if (!json) return this;

		let hotlineNumber: string[] = [];
		if (Array.isArray(json.hotlineNumber)) {
			hotlineNumber = [...json.hotlineNumber];
		}
		while (hotlineNumber.length < 3) {
			hotlineNumber.push("");
		}

		let closureExceptionConfig = json.closureExceptionConfig;
		if (typeof closureExceptionConfig === "string") {
			try {
				closureExceptionConfig = JSON.parse(closureExceptionConfig);
			} catch {
				closureExceptionConfig = null;
			}
		}

		Object.assign(this, {
			...json,
			createdAt: json.createdAt && new Date(json.createdAt),
			bannedAt: json.bannedAt && new Date(json.bannedAt),
			expireTime: json.expiredIn && new Date(json.expiredIn),
			expiredIn: json.expiredIn,
			hotlineNumber: hotlineNumber,
			workTime: normalizeWorkTimeValue(json.workTime),
			closureExceptionConfig: closureExceptionConfig ?? null,
		});

		// this.createUserName();

		return this;
	}
	parseList(jsons: any) {
		if (!jsons || !jsons.length) return;

		return jsons.map((item: any) => new Partner().parse(item));
	}

	createUserName() {
		// this.userName = [this.firstName, this.lastName].filter((val) => val).join(" ");
	}

	clone(): Partner {
		return new Partner({
			...this,
		});
	}
}

export class Store implements IModel<Store> {
	storeOwnerUser?: User | null;
	storeOwnerPartnerId?: number | null;
	/** Chế độ chuỗi (08/10/2026) — thiếu field = API cũ, FE coi như bật (xem cheDoGon.cheDoChuoiBat). */
	chainModeEnabled?: boolean | null;
	chainModeChangedAt?: string | null;

	constructor(input?: Partial<Store>) {
		this.storeOwnerUser = input?.storeOwnerUser ?? null;
	}

	parse(json: any): Store {
		if (!json) return this;
		Object.assign(this, {
			...json,
		});

		return this;
	}

	clone(): Store {
		return new Store({
			...this,
		});
	}
}
